import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Form,
  message,
  Modal,
  Pagination,
  Select,
  Transfer,
  Typography,
} from "antd";
import { Flex } from "common/components/flex/flex";
import { StatusTag } from "common/components/statusTag/statusTag";
import { customJobHandleSearch } from "utilities/generalUtility";
import { useDebounce } from "hooks/useDebounce";
import Job from "redux/models/job";
import Candidate from "redux/models/candidate";
import JobSelectOption from "common/components/jobSelect/jobSelect";
import "./assignCandidateClass.scss";

const { Title, Paragraph } = Typography;

const { Option } = Select;
const pageSize = 10;
export default function AssignCandidate({
  clientId,
  classId,
  classCandidates,
  setClassData,
  isModalVisible,
  setIsModalVisible,
}) {
  const [form] = Form.useForm();
  const [targetKeys, setTargetKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [jobList, setJobList] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  const [sourceList, setSourceList] = useState([]);
  const [sourceCandidatesById, setSourceCandidatesById] = useState({});
  const [sourcePage, setSourcePage] = useState(1);
  const [sourceSearch, setSourceSearch] = useState("");
  const [sourceTotal, setSourceTotal] = useState(0);

  const [targetCandidatesById, setTargetCandidatesById] = useState({});
  const [targetPage, setTargetPage] = useState(1);
  const [targetSearch, setTargetSearch] = useState("");

  const handleSourceSearch = useCallback((value) => {
    setSourcePage(1);
    setSourceSearch(value);
  }, []);
  const debouncedSetSourceSearch = useDebounce(handleSourceSearch, 400);

  const handleTargetSearch = useCallback((value) => {
    setTargetPage(1);
    setTargetSearch(value);
  }, []);
  const debouncedSetTargetSearch = useDebounce(handleTargetSearch, 400);

  const resetTransferState = () => {
    debouncedSetSourceSearch.cancel();
    debouncedSetTargetSearch.cancel();
    setTargetKeys([]);
    setSelectedKeys([]);
    setSourceList([]);
    setSourceCandidatesById({});
    setSourcePage(1);
    setSourceSearch("");
    setSourceTotal(0);
    setTargetCandidatesById({});
    setTargetPage(1);
    setTargetSearch("");
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSelectedJob(null);
    resetTransferState();
  };

  const onSelectChange = (sourceSelectedKeys, targetSelectedKeys) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      const payload = {
        classId,
        candidateJobEntryIds: targetKeys,
      };
      await Candidate.assignCandidate(payload);
      setClassData((prev) => {
        return { ...prev, shouldUpdateListing: !prev.shouldUpdateListing };
      });
      handleCancel();
      message.success("Candidate Assigned Successfully");
    } catch (error) {
      console.error(error);
    }

    setIsLoading(false);
  };

  const onChange = (newTargetKeys, direction, moveKeys) => {
    setTargetKeys(newTargetKeys);
    setTargetCandidatesById((prev) => {
      const updated = { ...prev };
      if (direction === "right") {
        moveKeys.forEach((key) => {
          const candidate = sourceCandidatesById[key];
          if (candidate) {
            updated[key] = candidate;
          }
        });
      } else {
        moveKeys.forEach((key) => {
          delete updated[key];
        });
      }
      return updated;
    });
  };

  const onJobChange = (value) => {
    setSelectedJob(value);
    resetTransferState();
  };

  const onSearch = (direction, value) => {
    if (direction === "left") {
      debouncedSetSourceSearch(value);
    } else {
      debouncedSetTargetSearch(value);
    }
  };

  const getJobsByClientIds = async () => {
    setIsLoading(true);
    try {
      const body = {
        clientIds: [clientId],
      };
      const res = await Job.getOnlyJobsByClientIds(body, false);

      setJobList(res);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const getCandidateByJobId = async () => {
    try {
      const params = {
        page: sourcePage,
        pageSize,
        search: sourceSearch || undefined,
        sortBy: "candidates.id",
        sortOrder: "asc",
        excludeIds: targetKeys.length ? targetKeys : undefined,
      };
      const data = await Candidate.v2.getByJobId(selectedJob, params);
      setSourceTotal(data.total);
      const updateData = data?.data.map((curr) => ({
        ...curr,
        disabled: classCandidates.includes(curr.id),
      }));

      setSourceList(updateData);
      setSourceCandidatesById((prev) => {
        const updated = { ...prev };
        updateData.forEach((item) => {
          updated[item.id] = item;
        });
        return updated;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const targetFilteredList = useMemo(() => {
    const allTargetCandidates = Object.values(targetCandidatesById);
    if (!targetSearch) {
      return allTargetCandidates;
    }
    const term = targetSearch.toLowerCase();
    return allTargetCandidates.filter((item) =>
      `${item?.candidate?.firstName ?? ""} ${item?.candidate?.lastName ?? ""}`
        .toLowerCase()
        .includes(term),
    );
  }, [targetCandidatesById, targetSearch]);

  const targetPageList = useMemo(() => {
    const start = (targetPage - 1) * pageSize;
    return targetFilteredList.slice(start, start + pageSize);
  }, [targetFilteredList, targetPage]);

  const dataSource = useMemo(
    () => [...sourceList, ...targetPageList],
    [sourceList, targetPageList],
  );

  useEffect(() => {
    getJobsByClientIds();
  }, []);

  useEffect(() => {
    if (selectedJob) {
      getCandidateByJobId();
    } else {
      setSourceList([]);
    }
  }, [selectedJob, sourcePage, sourceSearch, targetKeys]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sourceTotal / pageSize));
    if (sourcePage > maxPage) {
      setSourcePage(maxPage);
    }
  }, [sourceTotal, sourcePage]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(targetFilteredList.length / pageSize),
    );
    if (targetPage > maxPage) {
      setTargetPage(maxPage);
    }
  }, [targetFilteredList.length, targetPage]);

  return (
    <Modal
      open={isModalVisible}
      okText="Save"
      wrapClassName="vertical-center-modal"
      onCancel={handleCancel}
      centered
      destroyOnClose
      closeIcon={<i className="icon-closeable" />}
      width={1050}
      onOk={form.submit}
      okButtonProps={{
        loading: isLoading,
        disabled: targetKeys.length === 0,
      }}
      className="s2-theme-style assign-candidate-modal candidates-modals"
      title="Assign Candidate"
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        {/* <p className="instructions">Please </p> */}
        <Form.Item
          label="Select from"
          name="jobId"
          rules={[{ required: true, message: "Please select a job !" }]}
        >
          <Select
            showSearch
            allowClear
            onChange={onJobChange}
            optionFilterProp="children"
            filterOption={customJobHandleSearch}
            placeholder="Select Job"
            onSearch={(e) => console.log("Params", e)}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            size="large"
            className="select-job"
          >
            {jobList.map(({ id, title, client, jobLocation, extAtsId }) => (
              <Option key={id} value={id}>
                <JobSelectOption
                  title={title}
                  location={jobLocation.name}
                  client={client}
                  extAtsId={extAtsId}
                >
                  {title}
                </JobSelectOption>
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
      <Title level={5} className="rolesTableTypography">
        Select Candidate
      </Title>

      <Transfer
        oneWay
        showSearch
        className="rolesPermissionTransfer transfer-candidate"
        titles={["Source", "Target"]}
        dataSource={dataSource}
        targetKeys={targetKeys}
        selectedKeys={selectedKeys}
        rowKey={(record) => record.id}
        onChange={onChange}
        onSearch={onSearch}
        filterOption={() => true}
        onSelectChange={onSelectChange}
        footer={(currentItems, info) => {
          const isSource = info?.direction === "left";
          return (
            <Pagination
              current={isSource ? sourcePage : targetPage}
              showSizeChanger={false}
              total={isSource ? sourceTotal : targetFilteredList.length}
              pageSize={pageSize}
              onChange={isSource ? setSourcePage : setTargetPage}
            />
          );
        }}
        render={(item) => {
          const isDisabled = classCandidates.includes(item.id);
          return (
            <div className="candidate-names">
              <Flex gap={7} align="center" wrap="wrap">
                <Paragraph disabled={isDisabled} style={{ margin: 0 }}>
                  {`${item?.candidate?.firstName} ${item?.candidate?.lastName}`}
                </Paragraph>
                <StatusTag status={item?.status.name} />
                <StatusTag
                  status={item?.subStatus.name}
                  parentStatus={item?.status.name}
                />
              </Flex>
              <div className="tags" />
            </div>
          );
        }}
      />
    </Modal>
  );
}
